import React, { useState } from 'react';
import DateTimePicker from './DateTimePicker';

interface ReplayStartSelectorProps {
  isActive: boolean;
  onCancel: () => void;
  onDateTimeSelect: (timestamp: number) => void;
  minDate?: Date;
  maxDate?: Date;
  timezone?: string;
}

const ReplayStartSelector: React.FC<ReplayStartSelectorProps> = ({
  isActive,
  onCancel,
  onDateTimeSelect,
  minDate,
  maxDate,
  timezone = 'America/New_York'
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  if (!isActive) return null;

  return (
    <>
      {/* Instruction Overlay */}
      <div className="absolute top-16 sm:top-20 left-1/2 transform -translate-x-1/2 z-20 w-[calc(100vw-1rem)] sm:w-auto max-w-[min(860px,calc(100vw-1rem))] animate-fade-in-down">
        <div className="flex items-center gap-1.5 sm:gap-2 bg-slate-900/90 backdrop-blur-md border border-slate-700 p-1.5 sm:p-2 rounded-xl shadow-2xl max-w-[calc(100vw-1rem)] overflow-x-auto">
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded-lg transition-colors flex-shrink-0"
            title="Cancel and exit"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>

          <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700 flex-shrink-0">
            <div className="h-7 w-7 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <div className="text-xs sm:text-sm text-slate-100 font-semibold leading-tight">Start Replay</div>
              <div className="hidden sm:block text-[11px] text-slate-400 leading-tight">Click chart or pick a time</div>
            </div>
          </div>

          <div className="hidden sm:block h-6 w-px bg-slate-700 mx-1"></div>

          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-lg text-white text-xs sm:text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20 flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
            title="Select specific date and time"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Pick date/time
          </button>
        </div>
      </div>

      {/* Date Time Picker Modal */}
      <DateTimePicker
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        onSelect={(timestamp) => {
          onDateTimeSelect(timestamp);
          setIsDatePickerOpen(false);
        }}
        minDate={minDate}
        maxDate={maxDate}
        timezone={timezone}
      />
    </>
  );
};

export default ReplayStartSelector;
